/**
 * AdminCouponsTab Component
 * Coupon management tab for admin panel
 */
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Plus, Trash2, Copy, Loader2, Tag, CheckCircle, AlertCircle } from 'lucide-react';

const AdminCouponsTab = ({
  coupons,
  categories,
  filterTrack,
  setFilterTrack,
  filterUsed,
  setFilterUsed,
  isCouponDialogOpen,
  setIsCouponDialogOpen,
  newCoupon,
  setNewCoupon,
  couponLoading,
  handleCreateCoupon,
  handleDeleteCoupon,
  copyToClipboard,
  formatDate
}) => {
  const filteredCoupons = (coupons || []).filter(c => {
    const matchesTrack = !filterTrack || filterTrack === 'all' || c.track === filterTrack;
    const matchesUsed = !filterUsed || filterUsed === 'all' || 
      (filterUsed === 'used' && c.is_used) || 
      (filterUsed === 'unused' && !c.is_used);
    return matchesTrack && matchesUsed;
  });

  return (
    <Card className="bg-white dark:bg-slate-900">
      <CardHeader>
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <CardTitle className="flex items-center gap-2">
            <Tag className="w-5 h-5" />
            Coupon Management
          </CardTitle>
          <div className="flex gap-2 flex-wrap">
            <Select value={filterTrack || 'all'} onValueChange={(v) => setFilterTrack(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-[150px]" data-testid="filter-track">
                <SelectValue placeholder="All Tracks" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Tracks</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
                <SelectItem value="PMI_COMBO">PMI Combo</SelectItem>
                <SelectItem value="SCRUM_COMBO">Scrum Combo</SelectItem>
                <SelectItem value="ALL_ACCESS">All Access</SelectItem>
              </SelectContent>
            </Select>

            <Select value={filterUsed || 'all'} onValueChange={(v) => setFilterUsed(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-[150px]" data-testid="filter-status">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="used">Used</SelectItem>
                <SelectItem value="unused">Available</SelectItem>
              </SelectContent>
            </Select>

            <Dialog open={isCouponDialogOpen} onOpenChange={setIsCouponDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-[#0047AB] hover:bg-[#003380] text-white" data-testid="create-coupon-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  New Coupon
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create New Coupons</DialogTitle>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                  <div className="grid gap-2">
                    <Label>Course / Track</Label>
                    <Select
                      value={newCoupon.track}
                      onValueChange={(value) => setNewCoupon({ ...newCoupon, track: value })}
                    >
                      <SelectTrigger data-testid="coupon-track-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map(cat => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                        <SelectItem value="PMI_COMBO">PMI Combo (PMP+CAPM)</SelectItem>
                        <SelectItem value="SCRUM_COMBO">Scrum Combo (PSM+PSPO)</SelectItem>
                        <SelectItem value="ALL_ACCESS">All Access</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid gap-2">
                    <Label>Number of Coupons</Label>
                    <Input
                      type="number"
                      min="1"
                      max="100"
                      value={newCoupon.quantity}
                      onChange={(e) => setNewCoupon({ ...newCoupon, quantity: parseInt(e.target.value) || 1 })}
                      data-testid="coupon-quantity-input"
                    />
                  </div>

                  <div className="grid gap-2">
                    <Label>Validity</Label>
                    <Select
                      value={String(newCoupon.validity_months)}
                      onValueChange={(v) => setNewCoupon({...newCoupon, validity_months: parseInt(v)})}
                    >
                      <SelectTrigger data-testid="coupon-validity-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="3">3 Months</SelectItem>
                        <SelectItem value="6">6 Months</SelectItem>
                        <SelectItem value="12">12 Months</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid gap-2">
                    <Label>Discount</Label>
                    <Select value={String(newCoupon.discount_percent)} onValueChange={(v) => setNewCoupon({...newCoupon, discount_percent: parseInt(v)})}>
                      <SelectTrigger data-testid="coupon-discount-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="100">100% - Completely Free</SelectItem>
                        <SelectItem value="50">50% Discount (€14.50)</SelectItem>
                        <SelectItem value="30">30% Discount (€20.30)</SelectItem>
                        <SelectItem value="20">20% Discount (€23.20)</SelectItem>
                        <SelectItem value="10">10% Discount (€26.10)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg text-sm">
                    <strong>Coupon-Format:</strong> {newCoupon.track}-XXXXXX<br />
                    <strong>Discount:</strong> {newCoupon.discount_percent}%{newCoupon.discount_percent < 100 && ` (Final price: €${(29 * (1 - newCoupon.discount_percent / 100)).toFixed(2)})`}<br />
                    <strong>Valid for:</strong> {newCoupon.validity_months} months
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setIsCouponDialogOpen(false)}>Cancel</Button>
                  <Button onClick={handleCreateCoupon} disabled={couponLoading} className="bg-[#0047AB] hover:bg-[#003380]">
                    {couponLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      'Generate'
                    )}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <ScrollArea className="h-[500px]">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Code</TableHead>
                <TableHead>Track</TableHead>
                <TableHead>Discount</TableHead>
                <TableHead>Validity</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredCoupons.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-slate-500 py-8">
                    No coupons found
                  </TableCell>
                </TableRow>
              ) : (
                filteredCoupons.map((coupon) => (
                  <TableRow key={coupon.coupon_id} data-testid={`coupon-row-${coupon.coupon_id}`}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <code className="bg-slate-100 dark:bg-slate-800 px-2 py-1 rounded text-sm font-mono">
                          {coupon.code}
                        </code>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={() => copyToClipboard(coupon.code)}
                          data-testid={`copy-${coupon.coupon_id}`}
                        >
                          <Copy className="w-3 h-3" />
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{coupon.track}</Badge>
                    </TableCell>
                    <TableCell>
                      {coupon.discount_percent === 100 ? (
                        <Badge className="bg-green-500">Free</Badge>
                      ) : (
                        <span className="text-sm">{coupon.discount_percent}% off</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">{coupon.validity_months} months</TableCell>
                    <TableCell>
                      {coupon.is_used ? (
                        <div className="flex items-center gap-1 text-slate-500">
                          <CheckCircle className="w-4 h-4" />
                          <span className="text-sm">Used</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1 text-green-600">
                          <AlertCircle className="w-4 h-4" />
                          <span className="text-sm">Open</span>
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-slate-500">
                      {formatDate(coupon.created_at)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteCoupon(coupon.coupon_id)}
                        disabled={coupon.is_used}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid={`delete-coupon-${coupon.coupon_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </ScrollArea>
        <div className="mt-4 text-sm text-slate-500">
          {filteredCoupons.length} of {coupons.length} coupons shown
        </div>
      </CardContent>
    </Card>
  );
};

export default AdminCouponsTab;
